import { yupResolver } from "@hookform/resolvers/yup";
import { Box, DialogContent, Stack, Button, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { FormProvider, useForm } from "react-hook-form";
import useAppGlobal from "src/context/useAppGlobal";
import useBankInfoCreate from "src/hooks/bank-info/useBankInfoCreate";
import useBankInfoUpdate from "src/hooks/bank-info/useBankInfoUpdate";
import * as yup from "yup";
import FormControlInput from "src/components/shared/FormControlInput";
import { useEffect, useMemo } from "react";
import useBankList from "src/hooks/bank-info/useBankList";
import FormControlAutocomplete from "src/components/shared/FormControlAutocomplete";

function resolveBankId(bankField) {
  if (bankField == null || bankField === "") return "";
  if (typeof bankField === "string") return bankField;
  if (typeof bankField === "object") {
    if (bankField._id != null) return String(bankField._id);
    if (bankField.id != null) return String(bankField.id);
  }
  return "";
}

function bankCatalogRowId(row) {
  if (row == null) return "";
  return String(row._id ?? row.id ?? "");
}

function resolveBankIdFromItem(item) {
  if (!item) return "";
  const raw = item.bank ?? item.bankId ?? item.bank_id;
  return resolveBankId(raw);
}

function mapBankRowToOption(row) {
  const id = bankCatalogRowId(row);
  if (!id) return null;
  return {
    value: id,
    label: row.name,
    shortName: row.shortName,
    logo: row.logo,
    name: row.name,
  };
}

const validationSchema = yup.object().shape({
  bank: yup.object().required("Vui lòng chọn ngân hàng"),
  accountNumber: yup.string().required("Vui lòng nhập số tài khoản"),
  ownerName: yup.string().required("Vui lòng nhập tên chủ tài khoản"),
});

/** Kích thước khung logo ngân hàng (dropdown): 44×44 px */
const BANK_LOGO_FRAME_PX = 44;

export default function BankInfoForm({ item }) {
  const { closeDialog } = useAppGlobal();
  const { trigger: createBankInfo } = useBankInfoCreate();
  const { trigger: updateBankInfo } = useBankInfoUpdate(item?._id);
  const { data: banks } = useBankList();
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      bank: null,
      accountNumber: "",
      ownerName: "",
    },
    shouldUnregister: false,
  });
  const { handleSubmit, reset } = methods;

  const handleSubmitBankInfo = async (data) => {
    const payload = {
      bank: data?.bank?.value ?? "",
      accountNumber: data?.accountNumber ?? "",
      ownerName: data?.ownerName ?? "",
    };

    const res = item ? await updateBankInfo({ body: payload }) : await createBankInfo({ body: payload });

    if (res?.success) {
      reset({
        bank: null,
        accountNumber: "",
        ownerName: "",
      });
      closeDialog();
      enqueueSnackbar("Thành công!", { variant: "success" });
    } else {
      enqueueSnackbar(res?.error?.message || "Có lỗi xảy ra", { variant: "error" });
    }
  };

  const resolvedBankForForm = useMemo(() => {
    if (!item) return null;
    const bankId = resolveBankIdFromItem(item);
    const row = Array.isArray(banks?.data) ? banks.data.find((b) => bankCatalogRowId(b) === bankId) : null;
    const fromCatalog = mapBankRowToOption(row);
    if (fromCatalog) return fromCatalog;
    if (!bankId) return null;
    const name = item?.bank?.name ?? item?.bankName ?? "";
    return {
      value: bankId,
      label: (item?.bank?.shortName ?? "") + " - " + (item?.bank?.name ?? ""),
      shortName: item?.bank?.shortName,
      logo: item?.bank?.logo,
      name,
    };
  }, [item, banks?.data]);

  const catalogBankOptions = useMemo(
    () =>
      banks?.data
        ?.map((v) => ({
          value: bankCatalogRowId(v),
          label: v?.name,
          shortName: v?.shortName,
          logo: v?.logo,
          name: v?.name,
        }))
        ?.filter((o) => o.value) ?? [],
    [banks?.data],
  );

  const bankOptions = useMemo(() => {
    const opts = [...catalogBankOptions];
    if (!resolvedBankForForm?.value) return opts;
    const vid = String(resolvedBankForForm.value);
    if (!opts.some((o) => String(o.value) === vid)) {
      opts.unshift({ ...resolvedBankForForm, value: vid });
    }
    return opts;
  }, [catalogBankOptions, resolvedBankForForm]);

  useEffect(() => {
    if (!item) {
      reset({
        bank: null,
        accountNumber: "",
        ownerName: "",
      });
      return;
    }
    reset({
      bank: resolvedBankForForm,
      accountNumber: item?.accountNumber ?? "",
      ownerName: item?.ownerName ?? "",
    });
  }, [item, reset, resolvedBankForForm]);

  return (
    <DialogContent>
      <FormProvider {...methods}>
        <Stack
          spacing={2}
          pt={1.5}
        >
          <FormControlAutocomplete
            name="bank"
            label="Tên ngân hàng"
            options={bankOptions}
            getOptionLabel={(opt) => {
              if (!opt) return "";
              const short = opt.shortName?.trim();
              const full = (opt.name || opt.label)?.trim();
              if (short && full) return `${short} - ${full}`;
              return full || short || "";
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box
                  component="li"
                  key={key ?? option.value}
                  {...optionProps}
                  sx={{ display: "flex !important", alignItems: "center", gap: 1.25, py: 0.75 }}
                >
                  {option.logo ? (
                    <Box
                      sx={{
                        width: BANK_LOGO_FRAME_PX,
                        height: BANK_LOGO_FRAME_PX,
                        flexShrink: 0,
                        boxSizing: "border-box",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box
                        component="img"
                        src={option.logo}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                        sx={{
                          width: "100%",
                          height: "100%",
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ width: BANK_LOGO_FRAME_PX, height: BANK_LOGO_FRAME_PX, flexShrink: 0 }} />
                  )}
                  {option.logo && (option.shortName || option.name || option.label) ? (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.disabled"
                      sx={{ flexShrink: 0, userSelect: "none" }}
                      aria-hidden
                    >
                      {" "}
                      -{" "}
                    </Typography>
                  ) : null}
                  {option.shortName ? (
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ flexShrink: 0 }}
                    >
                      {option.shortName}
                    </Typography>
                  ) : null}
                  {option.shortName && (option.name || option.label) ? (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.disabled"
                      sx={{ flexShrink: 0, userSelect: "none" }}
                      aria-hidden
                    >
                      {" "}
                      -{" "}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {option.name || option.label}
                  </Typography>
                </Box>
              );
            }}
            required
          />
          <FormControlInput
            name="accountNumber"
            label="Số tài khoản"
            required
          />
          <FormControlInput
            name="ownerName"
            label="Chủ tài khoản"
            required
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            onClick={handleSubmit(handleSubmitBankInfo)}
          >
            Xác nhận
          </Button>
        </Stack>
      </FormProvider>
    </DialogContent>
  );
}
